import React, { useEffect, useState } from "react";
import Badge from "../Components/Badge";
import FormError from "../Components/FormError";
import {
  UNEXPECTED_RESPONSE_MESSAGE,
  parseJsonResponseBody,
  partitionPeopleFormErrors,
} from "../utils/apiErrors";
import { csrfToken } from "../utils/csrf";
import { stateBadgeVariant } from "../utils/userStateBadge";

/**
 * People directory: edit profile header + form + PATCH /people/:id.
 */
export default function PeopleForm({
  person,
  formOptions = { departments: [], roles: [] },
  canEditEmploymentFields = false,
  onClose,
  onSaved,
}) {
  const [department, setDepartment] = useState("");
  const [startOn, setStartOn] = useState("");
  const [endOn, setEndOn] = useState("");
  const [role, setRole] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState(
    /** @type {Record<string, string[]>} */ ({}),
  );
  const [generalErrors, setGeneralErrors] = useState([]);

  useEffect(() => {
    setDepartment(person.department ?? "");
    setStartOn(person.start_on ?? "");
    setEndOn(person.end_on ?? "");
    setRole(person.role ?? "");
    // Do not clear fieldErrors/generalErrors here — same as avoiding setErrors([]) on
    // person reference churn after a failed save. Reset on submit or drawer remount.
  }, [
    String(person.id),
    person.department,
    person.start_on,
    person.end_on,
    person.role,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFieldErrors({});
    setGeneralErrors([]);
    try {
      const res = await fetch(`/people/${person.id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken() ?? "",
        },
        body: JSON.stringify({
          user: {
            department: department || null,
            ...(canEditEmploymentFields
              ? { start_on: startOn || null, end_on: endOn || null, role }
              : {}),
          },
        }),
      });
      const text = await res.text();
      const parsed = parseJsonResponseBody(text);
      if (!parsed.ok) {
        setFieldErrors({});
        setGeneralErrors([UNEXPECTED_RESPONSE_MESSAGE]);
        return;
      }
      const data = parsed.data;
      if (!res.ok) {
        const { fieldErrors: nextField, generalMessages } =
          partitionPeopleFormErrors(data);
        setFieldErrors(nextField);
        setGeneralErrors(generalMessages);
        return;
      }
      onSaved(data);
    } catch {
      setFieldErrors({});
      setGeneralErrors(["Network error. Try again."]);
    } finally {
      setSaving(false);
    }
  };

  const { departments = [], roles = [] } = formOptions;

  const messagesFor = (attr) => fieldErrors[attr] ?? [];

  return (
    <>
      <header className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--color-border)] p-4">
        <div className="min-w-0">
          <h2
            id="people-edit-drawer-title"
            className="text-lg font-semibold text-[var(--color-foreground)]"
          >
            Edit profile
          </h2>
          <p className="truncate text-sm font-medium text-[var(--color-foreground)]">
            {person.full_name}
          </p>
          <p className="truncate text-sm text-[var(--color-muted)]">
            {person.email}
          </p>
          <div className="mt-2">
            <Badge variant={stateBadgeVariant(person.state)}>
              {person.state}
            </Badge>
          </div>
        </div>
        <button
          type="button"
          className="icon-btn shrink-0"
          aria-label="Close"
          onClick={onClose}
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <form
        className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4"
        onSubmit={handleSubmit}
      >
        {generalErrors.length > 0 ? (
          <div className="form-errors mb-4" role="alert">
            <h3>Could not save</h3>
            <ul>
              {generalErrors.map((msg, index) => (
                <li key={`${index}-${msg}`}>{msg}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="form-grid form-grid--flex-fill">
          <div className="form-row">
            <label htmlFor="people-edit-email">Email</label>
            <input
              id="people-edit-email"
              type="email"
              value={person.email ?? ""}
              disabled
              className="opacity-70"
            />
          </div>

          <div className="form-row">
            <label htmlFor="people-edit-department">Department</label>
            <select
              id="people-edit-department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              aria-invalid={messagesFor("department").length > 0}
              aria-describedby={
                messagesFor("department").length > 0
                  ? "people-edit-department-error"
                  : undefined
              }
            >
              <option value="">Select…</option>
              {departments.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <FormError
              id="people-edit-department-error"
              messages={messagesFor("department")}
            />
          </div>

          {canEditEmploymentFields ? (
            <>
              <div className="form-row">
                <label htmlFor="people-edit-start">Start date</label>
                <input
                  id="people-edit-start"
                  type="date"
                  value={startOn}
                  onChange={(e) => setStartOn(e.target.value)}
                  aria-invalid={messagesFor("start_on").length > 0}
                  aria-describedby={
                    messagesFor("start_on").length > 0
                      ? "people-edit-start-error"
                      : undefined
                  }
                />
                <FormError
                  id="people-edit-start-error"
                  messages={messagesFor("start_on")}
                />
              </div>

              <div className="form-row">
                <label htmlFor="people-edit-end">End date</label>
                <input
                  id="people-edit-end"
                  type="date"
                  value={endOn}
                  onChange={(e) => setEndOn(e.target.value)}
                  aria-invalid={messagesFor("end_on").length > 0}
                  aria-describedby={
                    messagesFor("end_on").length > 0
                      ? "people-edit-end-error"
                      : undefined
                  }
                />
                <FormError
                  id="people-edit-end-error"
                  messages={messagesFor("end_on")}
                />
              </div>

              <div className="form-row">
                <label htmlFor="people-edit-role">Portal Role</label>
                <select
                  id="people-edit-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  aria-invalid={messagesFor("role").length > 0}
                  aria-describedby={
                    messagesFor("role").length > 0
                      ? "people-edit-role-error"
                      : undefined
                  }
                >
                  {roles.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <FormError
                  id="people-edit-role-error"
                  messages={messagesFor("role")}
                />
              </div>
            </>
          ) : null}
        </div>

        <div className="form-actions mt-auto border-t border-[var(--color-border)] pt-4">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}
